/**
 * ChatPage.tsx — ADR-016 Task #6
 *
 * Dashboard chat (Asistente Agrovid, read-only).
 * El historial vive en useState — se resetea al navegar fuera.
 * El backend enriquece el system prompt con contexto real del tenant
 * (lotes, actividades recientes, tareas pendientes) antes de llamar al LLM.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Text, Button, Spinner, makeStyles, tokens, Textarea,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
  Input,
} from '@fluentui/react-components';
import {
  Send24Regular, Delete24Regular, Bot24Regular, Person24Regular, ArrowRight16Regular,
  Add20Regular, Edit16Regular, Delete16Regular, Navigation24Regular,
  ThumbLike16Regular, ThumbLike16Filled, ThumbDislike16Regular, ThumbDislike16Filled,
  ChevronLeft20Regular, ChevronRight20Regular,
} from '@fluentui/react-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import {
  sendChatMessage, listChatSessions, getChatSession, renameChatSession, deleteChatSession,
  setMessageFeedback,
} from '../api/dashboard.api';
import { useT } from '../i18n';
import { matchFallbackRoute, isUncertainReply } from '../utils/fallbackMatcher';
import type { ChatMessage, WebChatSessionSummary } from '../types/api.types';
import { useRanchoStore } from '../store/rancho.store';

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatChatDate = (iso: string): string => {
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

// ── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  page: {
    display: 'flex',
    height: 'calc(100vh - 120px)',
    maxWidth: '1100px',
    margin: '0 auto',
    gap: '16px',
  },
  sidebar: {
    width: '260px',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingRight: '12px',
    overflowY: 'auto',
  },
  sidebarCollapsed: {
    width: '44px',
    paddingRight: '0',
    alignItems: 'center',
    overflowX: 'hidden',
  },
  sidebarHeader: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '4px',
    marginBottom: '8px',
  },
  sessionInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  sessionDate: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
  },
  sessionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '8px 8px',
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    fontSize: '13px',
  },
  sessionRowActive: {
    backgroundColor: tokens.colorBrandBackground2,
  },
  sessionTitle: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  sessionActions: {
    display: 'flex',
    gap: '2px',
  },
  sidebarEmpty: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
    padding: '8px',
  },
  mobileToggle: {
    display: 'none',
    '@media (max-width: 720px)': {
      display: 'inline-flex',
    },
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    gap: '0',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 0 16px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  botIcon: {
    color: tokens.colorBrandForeground1,
    width: '32px',
    height: '32px',
  },
  messages: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  // Welcome card when no messages
  welcome: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: '12px',
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
    padding: '32px',
  },
  welcomeIcon: {
    fontSize: '48px',
    width: '48px',
    height: '48px',
    color: tokens.colorBrandForeground1,
    opacity: '0.5',
  },
  // Message bubbles
  msgRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'flex-start',
  },
  msgRowUser: {
    flexDirection: 'row-reverse',
  },
  avatarIcon: {
    flexShrink: 0,
    marginTop: '2px',
    width: '24px',
    height: '24px',
  },
  bubble: {
    maxWidth: '75%',
    padding: '10px 14px',
    borderRadius: '12px',
    fontSize: '14px',
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  bubbleAssistant: {
    background: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
    borderTopLeftRadius: '4px',
  },
  bubbleUser: {
    background: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    borderTopRightRadius: '4px',
  },
  // Typing indicator
  typing: {
    display: 'flex',
    gap: '4px',
    padding: '12px 14px',
    borderRadius: '12px',
    borderTopLeftRadius: '4px',
    background: tokens.colorNeutralBackground3,
    width: 'fit-content',
  },
  typingDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: tokens.colorNeutralForeground3,
    animationName: {
      '0%, 100%': { opacity: '0.3', transform: 'translateY(0)' },
      '50%':       { opacity: '1',   transform: 'translateY(-4px)' },
    },
    animationDuration: '1.2s',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
  },
  dot1: { animationDelay: '0s' },
  dot2: { animationDelay: '0.2s' },
  dot3: { animationDelay: '0.4s' },
  // Input area
  inputArea: {
    flexShrink: 0,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingTop: '12px',
    display: 'flex',
    gap: '8px',
    alignItems: 'flex-end',
  },
  textarea: {
    flex: 1,
  },
  errorBar: {
    padding: '8px 12px',
    background: tokens.colorStatusDangerBackground1,
    color: tokens.colorStatusDangerForeground1,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: '13px',
  },
  suggestions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    paddingTop: '8px',
  },
  suggestionChip: {
    fontSize: '12px',
  },
  // Fallback gracioso: tarjeta de navegación bajo la respuesta del asistente
  feedbackRow: {
    marginLeft: '32px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginTop: '2px',
  },
  fallbackCard: {
    marginLeft: '32px', // alinea con la burbuja (avatar 24px + gap 8px)
    marginTop: '-4px',
    padding: '8px 12px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
});

// ── Component ─────────────────────────────────────────────────────────────────

export function ChatPage() {
  const s = useStyles();
  const t = useT();
  const c = t.chat;
  const cs = c.sidebar;
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const qc = useQueryClient();
  const activeRanchoId = useRanchoStore(st => st.activeRanchoId);

  const [messages, setMessages]   = useState<ChatMessage[]>([]);
  const [input, setInput]         = useState('');
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [renamingId, setRenamingId]   = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deletingId, setDeletingId]   = useState<string | null>(null);
  const [savedFeedbackIdx, setSavedFeedbackIdx] = useState<number | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const sessionsQuery = useQuery({
    queryKey: ['chatSessions', tenantId],
    queryFn:  listChatSessions,
  });
  const sessions: WebChatSessionSummary[] = sessionsQuery.data ?? [];

  // Scroll to bottom whenever messages change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const mutation = useMutation({
    mutationFn: (userMessage: string) =>
      sendChatMessage({ message: userMessage, history: messages, sessionId, ...(activeRanchoId ? { ranchoId: activeRanchoId } : {}) }),
    onSuccess: (data) => {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: data.reply },
      ]);
      if (data.sessionId) setSessionId(data.sessionId);
      qc.invalidateQueries({ queryKey: ['chatSessions', tenantId] });
    },
  });

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text || mutation.isPending) return;
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    mutation.mutate(text);
  }, [input, mutation]);

  /** Voto 👍/👎 sobre una respuesta del asistente. Tocar el voto activo lo quita.
   * Actualización optimista: si el servidor falla, se revierte el voto previo. */
  const handleFeedback = useCallback(async (index: number, value: 'UP' | 'DOWN') => {
    if (!sessionId) return;
    const previous = messages[index]?.feedback;
    const next = previous === value ? null : value;
    setMessages(prev => prev.map((m, i) => (i === index ? { ...m, feedback: next ?? undefined } : m)));
    try {
      await setMessageFeedback(sessionId, index, next);
      setSavedFeedbackIdx(index);
    } catch {
      setMessages(prev => prev.map((m, i) => (i === index ? { ...m, feedback: previous } : m)));
    }
  }, [messages, sessionId]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = () => {
    setMessages([]);
    setSessionId(undefined);
    mutation.reset();
  };

  const handleSuggestion = (s: string) => {
    setInput(s);
  };

  const handleSelectSession = async (id: string) => {
    if (id === sessionId) return;
    const session = await getChatSession(id);
    setSessionId(session.sessionId);
    setMessages(session.messages);
    mutation.reset();
  };

  const startRename = (session: WebChatSessionSummary, e: React.MouseEvent) => {
    e.stopPropagation();
    setRenamingId(session.sessionId);
    setRenameValue(session.title);
  };

  const commitRename = async () => {
    if (!renamingId || !renameValue.trim()) { setRenamingId(null); return; }
    await renameChatSession(renamingId, renameValue.trim());
    setRenamingId(null);
    qc.invalidateQueries({ queryKey: ['chatSessions', tenantId] });
  };

  const confirmDelete = async () => {
    if (!deletingId) return;
    await deleteChatSession(deletingId);
    if (deletingId === sessionId) handleClear();
    setDeletingId(null);
    qc.invalidateQueries({ queryKey: ['chatSessions', tenantId] });
  };

  return (
    <div className={s.page}>
      {/* ── Sidebar de sesiones ─────────────────────────────────────────── */}
      <div className={`${s.sidebar} ${sidebarOpen ? '' : s.sidebarCollapsed}`}>
        <div className={s.sidebarHeader}>
          <Button
            appearance="subtle"
            size="small"
            icon={sidebarOpen ? <ChevronLeft20Regular /> : <ChevronRight20Regular />}
            aria-label={cs.toggle}
            title={cs.toggle}
            onClick={() => setSidebarOpen(o => !o)}
          />
          <Button
            appearance="outline"
            size="small"
            icon={<Add20Regular />}
            aria-label={cs.newChat}
            title={cs.newChat}
            onClick={handleClear}
          >
            {sidebarOpen ? cs.newChat : null}
          </Button>
        </div>
        {!sidebarOpen ? null : sessions.length === 0 ? (
          <Text className={s.sidebarEmpty}>{cs.empty}</Text>
        ) : (
          sessions.map(session => (
            <div
              key={session.sessionId}
              className={`${s.sessionRow} ${session.sessionId === sessionId ? s.sessionRowActive : ''}`}
              onClick={() => handleSelectSession(session.sessionId)}
            >
              {renamingId === session.sessionId ? (
                <Input
                  size="small"
                  value={renameValue}
                  autoFocus
                  onClick={e => e.stopPropagation()}
                  onChange={(_, d) => setRenameValue(d.value)}
                  onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenamingId(null); }}
                  onBlur={commitRename}
                />
              ) : (
                <div className={s.sessionInfo}>
                  <Text className={s.sessionTitle} title={session.title}>{session.title}</Text>
                  <Text className={s.sessionDate}>{formatChatDate(session.updatedAt)}</Text>
                </div>
              )}
              <div className={s.sessionActions}>
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<Edit16Regular />}
                  aria-label={cs.rename}
                  onClick={e => startRename(session, e)}
                />
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<Delete16Regular />}
                  aria-label={cs.delete}
                  onClick={e => { e.stopPropagation(); setDeletingId(session.sessionId); }}
                />
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={!!deletingId} onOpenChange={(_, d) => { if (!d.open) setDeletingId(null); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{cs.deleteConfirmTitle}</DialogTitle>
            <DialogContent>{cs.deleteConfirmBody}</DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeletingId(null)}>{cs.cancel}</Button>
              <Button appearance="primary" onClick={confirmDelete}>{cs.confirmDelete}</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* ── Panel de chat ───────────────────────────────────────────────── */}
      <div className={s.root}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className={s.header}>
        <div className={s.headerLeft}>
          <Button
            appearance="subtle"
            size="small"
            icon={<Navigation24Regular />}
            aria-label={cs.toggle}
            onClick={() => setSidebarOpen(o => !o)}
          />
          <Bot24Regular className={s.botIcon} />
          <div>
            <Text size={500} weight="semibold">{c.title}</Text>
            <br />
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{c.subtitle}</Text>
          </div>
        </div>
        {messages.length > 0 && (
          <Button
            appearance="subtle"
            size="small"
            icon={<Delete24Regular />}
            onClick={handleClear}
          >
            {c.clearBtn}
          </Button>
        )}
      </div>

      {!activeRanchoId && (
        <Text size={200} role="status" data-testid="chat-no-rancho-notice" style={{ padding: '6px 16px', background: tokens.colorPaletteYellowBackground1, color: tokens.colorPaletteYellowForeground1 }}>
          {c.noRanchoNotice}
        </Text>
      )}

      {/* ── Messages ────────────────────────────────────────────────────── */}
      <div className={s.messages}>
        {messages.length === 0 ? (
          <div className={s.welcome}>
            <Bot24Regular className={s.welcomeIcon} />
            <Text size={400} weight="semibold">{c.welcomeTitle}</Text>
            <Text size={200}>{c.welcomeBody}</Text>
            <div className={s.suggestions}>
              {Object.values(c.suggestions).map(sg => (
                <Button
                  key={sg}
                  size="small"
                  appearance="outline"
                  className={s.suggestionChip}
                  onClick={() => handleSuggestion(sg)}
                >
                  {sg}
                </Button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((msg, i) => {
            // Fallback gracioso: cuando el asistente declara que no tiene datos
            // suficientes (regla 2 del prompt en chat.handler.ts) y el mensaje del
            // usuario matchea una keyword conocida, se ofrece una tarjeta de
            // navegación en vez de dejar la respuesta genérica sola. Determinístico
            // — nunca se delega al LLM la elección del destino.
            const precedingUserMsg = msg.role === 'assistant' ? messages[i - 1] : undefined;
            const fallbackRoute = precedingUserMsg && isUncertainReply(msg.content)
              ? matchFallbackRoute(precedingUserMsg.content)
              : null;

            return (
              <div key={i}>
                <div className={`${s.msgRow} ${msg.role === 'user' ? s.msgRowUser : ''}`}>
                  {msg.role === 'assistant'
                    ? <Bot24Regular className={s.avatarIcon} style={{ color: tokens.colorBrandForeground1 }} />
                    : <Person24Regular className={s.avatarIcon} style={{ color: tokens.colorNeutralForeground3 }} />
                  }
                  <div className={`${s.bubble} ${msg.role === 'assistant' ? s.bubbleAssistant : s.bubbleUser}`}>
                    {msg.content}
                  </div>
                </div>
                {msg.role === 'assistant' && sessionId && (
                  <div className={s.feedbackRow}>
                    <Button
                      size="small"
                      appearance="subtle"
                      aria-label={c.feedbackUp}
                      aria-pressed={msg.feedback === 'UP'}
                      icon={msg.feedback === 'UP' ? <ThumbLike16Filled /> : <ThumbLike16Regular />}
                      onClick={() => handleFeedback(i, 'UP')}
                    />
                    <Button
                      size="small"
                      appearance="subtle"
                      aria-label={c.feedbackDown}
                      aria-pressed={msg.feedback === 'DOWN'}
                      icon={msg.feedback === 'DOWN' ? <ThumbDislike16Filled /> : <ThumbDislike16Regular />}
                      onClick={() => handleFeedback(i, 'DOWN')}
                    />
                    <span role="status" aria-live="polite">
                      <Text size={100}>{savedFeedbackIdx === i ? c.feedbackSaved : ''}</Text>
                    </span>
                  </div>
                )}
                {fallbackRoute && (
                  <div className={s.fallbackCard}>
                    {fallbackRoute.destination.type === 'web_page' ? (
                      <>
                        <Text size={200}>{c.fallbackWebHint}</Text>
                        <Button
                          size="small"
                          appearance="outline"
                          icon={<ArrowRight16Regular />}
                          iconPosition="after"
                          onClick={() => tenantId && navigate(`/${tenantId}${fallbackRoute.destination.webPath}`)}
                        >
                          {c.fallbackWebGo.replace('{label}', fallbackRoute.destination.labelKey ? c.fallbackLabels[fallbackRoute.destination.labelKey] : fallbackRoute.destination.label)}
                        </Button>
                      </>
                    ) : (
                      <Text size={200}>
                        {c.fallbackBotHint.replace('{command}', fallbackRoute.destination.command ?? '')}
                      </Text>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}

        {/* Typing indicator */}
        {mutation.isPending && (
          <div className={s.msgRow}>
            <Bot24Regular className={s.avatarIcon} style={{ color: tokens.colorBrandForeground1 }} />
            <div className={s.typing}>
              <div className={`${s.typingDot} ${s.dot1}`} />
              <div className={`${s.typingDot} ${s.dot2}`} />
              <div className={`${s.typingDot} ${s.dot3}`} />
            </div>
          </div>
        )}

        {/* Error */}
        {mutation.isError && (
          <div className={s.errorBar}>
            ⚠ {(mutation.error as Error)?.message ?? c.errorGeneric}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* ── Input ───────────────────────────────────────────────────────── */}
      <div className={s.inputArea}>
        <Textarea
          className={s.textarea}
          placeholder={c.inputPlaceholder}
          value={input}
          onChange={(_, d) => setInput(d.value)}
          onKeyDown={handleKeyDown}
          resize="vertical"
          rows={2}
          disabled={mutation.isPending}
        />
        <Button
          appearance="primary"
          icon={mutation.isPending ? <Spinner size="tiny" /> : <Send24Regular />}
          onClick={handleSend}
          disabled={!input.trim() || mutation.isPending}
          aria-label={c.sendBtn}
        />
      </div>
      </div>
    </div>
  );
}
